import { useEffect } from 'react'
import { useApp } from '../context/AppContext.jsx'

// Larguras da sidebar em px — devem espelhar globals.css (.sidebar / .collapsed).
const SIDEBAR_EXPANDED = 222

/**
 * useCollapseSidebarOnOverlap(minContentWidth, options?)
 *
 * Recolhe a sidebar automaticamente APENAS quando ela, aberta, não deixaria
 * largura suficiente para o conteúdo (ex.: um calendário). Se houver espaço,
 * a sidebar permanece como o usuário deixou. Ao desmontar a página o
 * recolhimento forçado é liberado, restaurando a preferência manual.
 *
 * Reutilizável: qualquer página com calendário (ou qualquer conteúdo largo)
 * chama este hook passando a largura mínima que o conteúdo precisa. Sem lógica
 * específica de página aqui — só a conta de sobreposição.
 *
 * @param {number} minContentWidth  Largura mínima (px) que o conteúdo precisa
 *   para renderizar sem ser espremido pela sidebar aberta.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]  Desliga o cálculo quando false
 *   (ex.: view de calendário inativa numa página multi-view).
 */
export function useCollapseSidebarOnOverlap(minContentWidth, options = {}) {
  const { enabled = true } = options
  const { setSidebarForcedCollapsed } = useApp()

  useEffect(() => {
    if (!enabled) {
      setSidebarForcedCollapsed(false)
      return
    }

    // Espaço restante para o conteúdo se a sidebar estiver ABERTA.
    // Se já couber com ela aberta, nada a forçar. Se não couber, comparamos
    // também com a sidebar recolhida: só vale a pena forçar o recolhimento se
    // isso realmente destrava o espaço necessário.
    const check = () => {
      const vw = window.innerWidth
      const availExpanded = vw - SIDEBAR_EXPANDED
      const overlaps = availExpanded < minContentWidth
      setSidebarForcedCollapsed(overlaps)
    }

    check()
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('resize', check)
      // Libera o recolhimento forçado ao sair da página / desabilitar.
      setSidebarForcedCollapsed(false)
    }
  }, [enabled, minContentWidth, setSidebarForcedCollapsed])
}


